// ---------------------------------------------------------------------------
// GOHIGHLEVEL IMPLEMENTATION PLAN — single source of truth.
//
// This file answers one question precisely: what is ALREADY wired in code, and
// what still has to be clicked together by a human inside the GoHighLevel UI.
//
// NOTHING here is retyped from elsewhere. Tags, pipeline stages and audience
// nurture names are DERIVED from src/lib/crmForms.ts (GHL_TAGS,
// GHL_PIPELINE_STAGE, AUDIENCE_META), which is the same map the deployed
// `crm-capture` edge function matches on. If a tag is renamed there, this
// checklist changes with it and cannot go stale.
// ---------------------------------------------------------------------------

import {
  GHL_TAGS,
  GHL_PIPELINE_STAGE,
  AUDIENCE_META,
  type FormType,
  type Audience,
} from '@/lib/crmForms';

/**
 * done   — running in production right now, no action required
 * manual — the platform already SENDS the trigger; someone must build the
 *          matching workflow/campaign inside GoHighLevel
 * todo   — not built on either side yet
 */
export type GhlStatus = 'done' | 'manual' | 'todo';

export interface GhlItem {
  title: string;
  /** What it does / what the operator has to do. */
  detail: string;
  status: GhlStatus;
  /** Where it lives — edge function, component, or "GoHighLevel UI". */
  where?: string;
}

export interface GhlSection {
  id: string;
  title: string;
  intro: string;
  items: GhlItem[];
}

/* ========================================================================= */
/* 1. INTEGRATIONS ALREADY COMPLETED (code side)                              */
/* ========================================================================= */

export const GHL_COMPLETED: GhlItem[] = [
  {
    title: 'Protected capture endpoint',
    detail:
      'crm-capture verifies Turnstile, rate limits by IP / visitor / email / phone, validates against an allow-list, stores the consent snapshot, then upserts the GoHighLevel contact (dedupe on email, then phone). GHL credentials never reach the browser.',
    status: 'done',
    where: 'edge: crm-capture',
  },
  {
    title: 'Automatic retry queue',
    detail:
      'A GHL outage cannot lose a contact: failed pushes are written to mkt_crm_queue and replayed with exponential backoff by the crm-retry function.',
    status: 'done',
    where: 'edge: crm-retry',
  },
  {
    title: 'Sitewide newsletter / visitor capture',
    detail:
      'LeadCaptureForm (name, email, optional phone, optional trade, SMS opt-in checked by default with the STOP disclaimer) renders in the footer on every page, so it is live on the home page, all SEO hubs, trade pages, city pages and resources.',
    status: 'done',
    where: 'src/components/marketing/LeadCaptureForm.tsx',
  },
  {
    title: 'Registration sync',
    detail:
      'A completed signup pushes name, email, phone, trade, user type, city, state and service areas to GoHighLevel, then fires a registration-completed activity.',
    status: 'done',
    where: 'src/contexts/AuthContext.tsx',
  },
  {
    title: 'Abandoned registration capture',
    detail:
      'A signup that never confirms is pushed as registration-started, tagged GigGone Unverified + nurture:abandoned-registration, so GHL has something to follow up on.',
    status: 'done',
    where: 'src/contexts/AuthContext.tsx',
  },
  {
    title: 'Lead activity events',
    detail:
      'lead-viewed (once per lead per session), lead-purchased, project-posted, registration-completed, email-verified and profile-updated are pushed with trade, city, state and the listing reference.',
    status: 'done',
    where: 'src/lib/crmForms.ts + src/lib/marketplace.ts',
  },
  {
    title: 'Purchase + subscription sync',
    detail:
      'The payment webhook is the only grant path; a settled payment tags the contact as a paid customer and records the plan, so membership campaigns can segment on it.',
    status: 'done',
    where: 'edge: payment-webhook',
  },
  {
    title: 'SMS consent storage',
    detail:
      'sms_opt_in is captured on every form (checked by default, with the required disclaimer) and written to the contact, so GHL SMS steps are legally sendable instead of silently skipped.',
    status: 'done',
    where: 'edge: crm-capture',
  },
  {
    title: 'Attribution',
    detail:
      'First-touch and last-touch source, landing page, referrer and all five UTM parameters ride along with every contact for remarketing and ROI reporting.',
    status: 'done',
    where: 'src/lib/tracking.ts',
  },
];

/* ========================================================================= */
/* 2. CUSTOM FIELDS CURRENTLY SYNCHRONIZED                                    */
/* ========================================================================= */

/** Field key sent to GHL -> what it contains. Create these in GHL once. */
export const GHL_CUSTOM_FIELDS: { key: string; detail: string }[] = [
  { key: 'user_type', detail: 'homeowner | gc | subcontractor | lead' },
  { key: 'company_name', detail: 'Company on the profile (contractors/subs)' },
  { key: 'primary_trade', detail: 'Human label, e.g. "Kitchen Remodel"' },
  { key: 'trade_slug', detail: 'Machine slug, e.g. kitchen-remodel — use for by-trade campaigns' },
  { key: 'service_city', detail: 'City on the profile or the posted project' },
  { key: 'service_state', detail: 'Two-letter state' },
  { key: 'postal_code', detail: 'ZIP where supplied' },
  { key: 'language', detail: 'en | es — send Spanish contacts the Spanish sequence' },
  { key: 'last_activity', detail: 'Most recent behaviour: lead-viewed, lead-purchased, project-posted…' },
  { key: 'registration_status', detail: 'started | completed — drives abandoned-registration exit' },
  { key: 'plan', detail: 'basic | pro | premium | single' },
  { key: 'payment_status', detail: 'paid | failed | none' },
  { key: 'sms_opt_in', detail: 'true | false — gate every SMS step on this' },
  { key: 'visitor_id', detail: 'Stable anonymous id, links pre-signup browsing to the contact' },
  { key: 'utm_source / utm_medium / utm_campaign / utm_term / utm_content', detail: 'Full campaign attribution' },
  { key: 'landing_page / referrer / first_seen / last_seen', detail: 'First- and last-touch context' },
];

/* ========================================================================= */
/* 3. TAGS APPLIED AUTOMATICALLY — derived, never retyped                     */
/* ========================================================================= */

/** Every static tag the platform can apply, de-duplicated and sorted. */
export const ghlAllTags = (): string[] => {
  const set = new Set<string>();
  (Object.keys(GHL_TAGS) as FormType[]).forEach((k) => GHL_TAGS[k].forEach((tag) => set.add(tag)));
  (Object.keys(AUDIENCE_META) as Audience[]).forEach((a) =>
    AUDIENCE_META[a].tags.forEach((tag) => set.add(tag))
  );
  return [...set].sort((a, b) => a.localeCompare(b));
};

/** Dynamic tag families generated per contact at capture time. */
export const GHL_DYNAMIC_TAGS: { pattern: string; detail: string }[] = [
  { pattern: 'stage:<Pipeline Stage>', detail: 'Mirrors the pipeline stage for the submitted form' },
  { pattern: 'activity:<event>', detail: 'activity:lead-viewed, activity:lead-purchased, …' },
  { pattern: 'audience:<type>', detail: 'audience:homeowner | audience:gc | audience:subcontractor | audience:visitor' },
  { pattern: 'trade:<slug>', detail: 'trade:roofing, trade:hvac — the segment for weekly by-trade sends' },
  { pattern: 'state:<XX> / city:<name>', detail: 'Geographic segmentation for local campaigns' },
  { pattern: 'nurture:<sequence>', detail: 'Names the onboarding sequence the contact should enter' },
  { pattern: 'GigGone SMS Opt In', detail: 'Applied only when the visitor left the consent box checked' },
];

/** Every pipeline stage the platform emits, in a sensible board order. */
export const ghlAllStages = (): string[] => {
  const seen = new Set<string>();
  const order = [
    'Registration Started',
    'New Registration',
    'Registered',
    'Engaged',
    'New Inquiry',
    'Support Request',
    'New Project',
    'Work Order Created',
    'Checkout Started',
    'Lead Purchased',
    'Subscriber',
  ];
  (Object.keys(GHL_PIPELINE_STAGE) as FormType[]).forEach((k) => seen.add(GHL_PIPELINE_STAGE[k]));
  const known = order.filter((s) => seen.has(s));
  const extra = [...seen].filter((s) => !order.includes(s)).sort();
  return [...known, ...extra];
};

/* ========================================================================= */
/* 4. RECOMMENDED PIPELINES                                                   */
/* ========================================================================= */

export interface GhlPipeline {
  name: string;
  purpose: string;
  stages: string[];
  /** Tag that should create the opportunity. */
  entry: string;
}

export const GHL_PIPELINES: GhlPipeline[] = [
  {
    name: 'Contractor Acquisition',
    purpose: 'Every GC / subcontractor from first visit to paying member. This is the revenue board.',
    entry: 'GigGone Contractor',
    stages: [
      'Visitor Captured',
      'Registration Started',
      'Registered — No Purchase',
      'Viewed a Lead',
      'Checkout Started',
      'Single Lead Buyer',
      'Member — Basic',
      'Member — Pro',
      'Member — Premium',
      'Churned / Past Due',
    ],
  },
  {
    name: 'Homeowner Projects',
    purpose: 'Supply side. Tracks a homeowner from signup to a posted project that subs can buy.',
    entry: 'GigGone Homeowner',
    stages: [
      'Visitor Captured',
      'Registered',
      'Project Posted',
      'Project Live',
      'Lead Sold (1+ sub)',
      'Fully Sold (6/6)',
      'Expired / No Interest',
    ],
  },
  {
    name: 'Membership Lifecycle',
    purpose: 'Retention board for existing members: renewals, credit burn, upgrades, dunning.',
    entry: 'GigGone Paid',
    stages: [
      'Active — Credits Unused',
      'Active — Credits Half Spent',
      'Active — Credits Exhausted',
      'Upgrade Offered',
      'Past Due / Dunning',
      'Cancelled',
      'Win-Back',
    ],
  },
  {
    name: 'Support & Inquiries',
    purpose: 'Contact form, callbacks and disputes, so nothing sits unanswered.',
    entry: 'GigGone Contact Request',
    stages: ['New Inquiry', 'Contacted', 'Waiting on Customer', 'Resolved'],
  },
];

/* ========================================================================= */
/* 5. WORKFLOWS TO BUILD IN GOHIGHLEVEL                                       */
/* ========================================================================= */

export interface GhlWorkflow {
  name: string;
  audience: string;
  trigger: string;
  steps: string[];
  status: GhlStatus;
  channel: 'email' | 'sms' | 'both';
}

export const GHL_WORKFLOWS: GhlWorkflow[] = [
  {
    name: 'Homeowner Onboarding',
    audience: 'Homeowners',
    trigger: 'Tag added: nurture:homeowner-onboarding',
    channel: 'email',
    status: 'manual',
    steps: [
      'Day 0 — Welcome + how posting a project works (free)',
      'Day 1 — "Post your project" nudge if no project posted',
      'Day 3 — What to expect when subs unlock your project',
      'Day 7 — Safety: licences, insurance, deposits, red flags',
      'Exit when tag GigGone Posted Project is added',
    ],
  },
  {
    name: 'Contractor Onboarding',
    audience: 'General contractors',
    trigger: 'Tag added: nurture:contractor-onboarding',
    channel: 'both',
    status: 'manual',
    steps: [
      'Day 0 — Welcome + how leads and credits work ($29 flat, no surge)',
      'Day 1 — Set your trade + service area so we match you correctly',
      'Day 2 — SMS (consent only): "3 new projects in your area this week"',
      'Day 4 — Plan comparison: single lead vs Basic vs Pro vs Premium',
      'Day 7 — Case study + first-lead offer',
      'Exit when tag GigGone Paid is added',
    ],
  },
  {
    name: 'Subcontractor Onboarding',
    audience: 'Subcontractors',
    trigger: 'Tag added: nurture:subcontractor-onboarding',
    channel: 'both',
    status: 'manual',
    steps: [
      'Day 0 — Welcome + what a lead unlocks (city, ZIP, contacts, plans)',
      'Day 1 — Pick your trades so weekly opportunities are relevant',
      'Day 3 — Speed to contact wins: call within the hour',
      'Day 5 — Membership maths: cost per booked job, not cost per lead',
      'Exit when tag GigGone Paid is added',
    ],
  },
  {
    name: 'Abandoned Registration',
    audience: 'Anyone who started but never confirmed',
    trigger: 'Tag added: GigGone Registration Started AND custom field registration_status = started',
    channel: 'both',
    status: 'manual',
    steps: [
      '1 hour — "Finish setting up your account" + resend confirmation',
      'Day 1 — SMS (consent only): one-line finish-signup reminder',
      'Day 3 — Objection handling: free to browse, only pay to unlock',
      'Day 7 — Final nudge, then move to long-term newsletter',
      'HARD EXIT when registration_status = completed or tag GigGone Registered',
    ],
  },
  {
    name: 'Lead Purchase Follow-Up',
    audience: 'Lead buyers',
    trigger: 'Tag added: GigGone Purchased Lead',
    channel: 'both',
    status: 'manual',
    steps: [
      'Immediately — Receipt + the contact details + "call within the hour"',
      'Hour 2 — SMS (consent only): reminder to make contact',
      'Day 2 — Did you reach them? Quick outcome survey',
      'Day 5 — Won it? Ask for a testimonial. Lost it? Offer another lead',
      'Day 10 — Upgrade offer if they are buying singles repeatedly',
    ],
  },
  {
    name: 'Weekly Lead Opportunities by Trade',
    audience: 'Contractors + subcontractors with a trade on file',
    trigger: 'Recurring weekly, filtered on trade:<slug> tag',
    channel: 'email',
    status: 'manual',
    steps: [
      'One send per trade segment, Tuesday morning',
      'Pull open projects for that trade + service state',
      'Suppress anyone with zero credits — send the upgrade variant instead',
    ],
  },
  {
    name: 'Visitor Newsletter Nurture',
    audience: 'Newsletter / footer signups with no account',
    trigger: 'Tag added: nurture:visitor-nurture',
    channel: 'email',
    status: 'manual',
    steps: [
      'Day 0 — Welcome, what GIGGONE is, pick your side (homeowner or trade)',
      'Day 4 — Audience-splitting click: re-tag based on which link they press',
      'Weekly — Trade content, then a create-account CTA',
      'Exit when tag GigGone Registered is added',
    ],
  },
  {
    name: 'Credits Running Low',
    audience: 'Members',
    trigger: 'Custom field credits_remaining <= 1 (set from the balance sync)',
    channel: 'email',
    status: 'todo',
    steps: [
      'Warn before the cycle ends: unused credits expire and do not roll over',
      'Offer a single $29 lead or a plan upgrade',
      'Premium members: remind them the 12-month term is already covering it',
    ],
  },
  {
    name: 'Renewal & Dunning',
    audience: 'Members past due',
    trigger: 'Tag added: GigGone Past Due (payment webhook)',
    channel: 'both',
    status: 'manual',
    steps: [
      'Day 0 — Payment failed, lead delivery paused, update card',
      'Day 2 / Day 5 — Retry reminders (SMS on day 2 with consent)',
      'Day 10 — Cancellation notice + win-back offer',
    ],
  },
  {
    name: 'Win-Back',
    audience: 'Cancelled members',
    trigger: 'Tag added: GigGone Cancelled',
    channel: 'email',
    status: 'todo',
    steps: [
      'Day 7 — What you are missing: live project count in their trade',
      'Day 30 — Single-lead offer to re-engage without a commitment',
      'Day 90 — Quarterly check-in',
    ],
  },
];

/* ========================================================================= */
/* 6. SMART LISTS                                                             */
/* ========================================================================= */

export const GHL_SMART_LISTS: { name: string; filter: string }[] = [
  { name: 'Unverified Registrations', filter: 'Tag GigGone Unverified AND registration_status = started' },
  { name: 'Registered — Never Purchased', filter: 'Tag GigGone Registered AND NOT tag GigGone Paid' },
  { name: 'Viewed but Never Bought', filter: 'Tag GigGone Viewed Lead AND NOT tag GigGone Lead Buyer' },
  { name: 'Single-Lead Repeat Buyers', filter: 'Tag GigGone Lead Buyer AND plan = single — prime upgrade targets' },
  { name: 'Active Members by Plan', filter: 'Tag GigGone Paid, split by plan = basic | pro | premium' },
  { name: 'Homeowners with No Project', filter: 'Tag GigGone Homeowner AND NOT tag GigGone Posted Project' },
  { name: 'SMS Reachable', filter: 'sms_opt_in = true AND phone is not empty — the only list SMS may send to' },
  { name: 'Spanish Speakers', filter: 'language = es — receives the Spanish variant of every sequence' },
  { name: 'By Trade', filter: 'One list per trade:<slug> tag, feeds the weekly opportunities send' },
  { name: 'Past Due / Dunning', filter: 'payment_status = failed' },
];

/* ========================================================================= */
/* 7. CAMPAIGN INVENTORY                                                      */
/* ========================================================================= */

export const GHL_EMAIL_ACTIVE: GhlItem[] = [
  {
    title: 'Transactional email (platform-sent, not GHL)',
    detail:
      'Confirmation, password reset, job alert digests and payment receipts are sent by the platform through the verified sending domain — independent of any GHL campaign, so they keep working regardless of CRM configuration.',
    status: 'done',
    where: 'edge: send-transactional-email / job-alerts-digest',
  },
  {
    title: 'Marketing sequences',
    detail:
      'No GoHighLevel email campaign is live yet. The triggers, tags and custom fields they need are all already arriving — the sequences themselves must be built in the GHL UI.',
    status: 'manual',
    where: 'GoHighLevel UI',
  },
];

export const GHL_SMS_ACTIVE: GhlItem[] = [
  {
    title: 'Consent capture + storage',
    detail:
      'Opt-in is collected with the required disclaimer and stored as sms_opt_in on the contact, so a workflow can legally send.',
    status: 'done',
    where: 'edge: crm-capture',
  },
  {
    title: 'Phone verification',
    detail: 'Twilio Verify is wired for account phone verification.',
    status: 'done',
    where: 'edge: account-verification',
  },
  {
    title: 'Marketing SMS workflows',
    detail:
      'None are live. Build them in GHL against the "SMS Reachable" smart list — every SMS step must be gated on sms_opt_in = true and include STOP handling.',
    status: 'manual',
    where: 'GoHighLevel UI',
  },
];

/* ========================================================================= */
/* SUMMARY COUNTS (rendered by the admin panel)                               */
/* ========================================================================= */

export const ghlCounts = () => {
  const all = [...GHL_COMPLETED, ...GHL_EMAIL_ACTIVE, ...GHL_SMS_ACTIVE];
  const wf = GHL_WORKFLOWS;
  return {
    done: all.filter((i) => i.status === 'done').length,
    manual: all.filter((i) => i.status === 'manual').length + wf.filter((w) => w.status === 'manual').length,
    todo: wf.filter((w) => w.status === 'todo').length,
    tags: ghlAllTags().length,
    fields: GHL_CUSTOM_FIELDS.length,
    pipelines: GHL_PIPELINES.length,
    workflows: wf.length,
    lists: GHL_SMART_LISTS.length,
  };
};
